import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, TrendingDown, Minus, Calendar, Clock,
  ThermometerSun, Heart, Wind, Brain, Utensils, Moon, Droplets,
  AlertTriangle, CheckCircle2, Plus, Save, ChevronRight, LineChart
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SYMPTOMS = [
  { id: 'pain', name: 'Pain', icon: Activity, color: '#ef4444', description: 'Overall pain level' },
  { id: 'breathing', name: 'Breathing', icon: Wind, color: '#3b82f6', description: 'Shortness of breath' },
  { id: 'nausea', name: 'Nausea', icon: Utensils, color: '#22c55e', description: 'Nausea/vomiting' },
  { id: 'anxiety', name: 'Anxiety', icon: Brain, color: '#a855f7', description: 'Anxiety level' },
  { id: 'fatigue', name: 'Fatigue', icon: Moon, color: '#f59e0b', description: 'Energy/tiredness' },
  { id: 'appetite', name: 'Appetite', icon: Utensils, color: '#06b6d4', description: 'Appetite level' },
];

const SEVERITY_LABELS = ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'];

const getSeverityColor = (score) => {
  if (score <= 2) return 'text-green-500';
  if (score <= 4) return 'text-yellow-500';
  if (score <= 6) return 'text-orange-500';
  return 'text-red-500';
};

const getSeverityBg = (score) => {
  if (score <= 2) return 'bg-green-100 border-green-300';
  if (score <= 4) return 'bg-yellow-100 border-yellow-300';
  if (score <= 6) return 'bg-orange-100 border-orange-300';
  return 'bg-red-100 border-red-300';
};

export function SymptomDiary({ patientId }) {
  const [entries, setEntries] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [currentSymptoms, setCurrentSymptoms] = useState(
    SYMPTOMS.reduce((acc, s) => ({ ...acc, [s.id]: 0 }), {})
  );
  const [notes, setNotes] = useState('');
  const [selectedSymptom, setSelectedSymptom] = useState(null);
  const [trendData, setTrendData] = useState([]);

  // Generate sample trend data
  useEffect(() => {
    const data = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        pain: Math.floor(Math.random() * 4) + 2,
        breathing: Math.floor(Math.random() * 3) + 1,
        nausea: Math.floor(Math.random() * 3),
        anxiety: Math.floor(Math.random() * 4) + 1,
        fatigue: Math.floor(Math.random() * 3) + 3,
        appetite: Math.floor(Math.random() * 3) + 2,
      });
    }
    setTrendData(data);
  }, []);

  const handleSaveEntry = () => {
    const entry = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      symptoms: { ...currentSymptoms },
      notes,
      overall: Math.round(Object.values(currentSymptoms).reduce((a, b) => a + b, 0) / SYMPTOMS.length)
    };

    setEntries(prev => [entry, ...prev]);
    setCurrentSymptoms(SYMPTOMS.reduce((acc, s) => ({ ...acc, [s.id]: 0 }), {}));
    setNotes('');
    setShowAddDialog(false);
    toast.success('Symptom entry saved');
  };

  const getOverallTrend = () => {
    if (trendData.length < 2) return 'stable';
    const recent = trendData.slice(-3);
    const older = trendData.slice(-6, -3);
    const recentAvg = recent.reduce((acc, d) => acc + d.pain, 0) / recent.length;
    const olderAvg = older.reduce((acc, d) => acc + d.pain, 0) / (older.length || 1);
    
    if (recentAvg > olderAvg + 1) return 'worsening';
    if (recentAvg < olderAvg - 1) return 'improving';
    return 'stable';
  };

  const trend = getOverallTrend();

  return (
    <div className="space-y-6 p-4" data-testid="symptom-diary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Symptom Diary</h2>
          <p className="text-muted-foreground">Track daily symptoms and patterns</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-symptom-entry">
          <Plus className="w-4 h-4 mr-2" />
          Log Today's Symptoms
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Overall Trend</p>
                <p className="text-xl font-bold capitalize">{trend}</p>
              </div>
              {trend === 'improving' && <TrendingDown className="w-8 h-8 text-green-500" />}
              {trend === 'worsening' && <TrendingUp className="w-8 h-8 text-red-500" />}
              {trend === 'stable' && <Minus className="w-8 h-8 text-yellow-500" />}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Entries This Week</p>
                <p className="text-xl font-bold">{entries.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Most Improved</p>
                <p className="text-xl font-bold">Breathing</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Needs Attention</p>
                <p className="text-xl font-bold">Pain</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LineChart className="w-5 h-5" />
            14-Day Symptom Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis domain={[0, 10]} fontSize={12} />
                <Tooltip />
                <Legend />
                {SYMPTOMS.map(symptom => (
                  <Line
                    key={symptom.id}
                    type="monotone"
                    dataKey={symptom.id}
                    name={symptom.name}
                    stroke={symptom.color}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Current Symptom Status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Symptom Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SYMPTOMS.map(symptom => {
              const Icon = symptom.icon;
              const currentValue = trendData[trendData.length - 1]?.[symptom.id] || 0;
              const prevValue = trendData[trendData.length - 2]?.[symptom.id] || 0;
              const change = currentValue - prevValue;
              
              return (
                <div
                  key={symptom.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${getSeverityBg(currentValue)}`}
                  onClick={() => setSelectedSymptom(symptom)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5" style={{ color: symptom.color }} />
                      <span className="font-medium">{symptom.name}</span>
                    </div>
                    {change !== 0 && (
                      <Badge variant={change > 0 ? 'destructive' : 'default'} className="text-xs">
                        {change > 0 ? '+' : ''}{change}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${getSeverityColor(currentValue)}`}>
                      {currentValue}
                    </span>
                    <span className="text-sm text-muted-foreground">/10</span>
                  </div>
                  <Progress 
                    value={currentValue * 10} 
                    className="h-2 mt-2"
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No symptom entries yet.</p>
              <p className="text-sm">Start tracking to see patterns over time.</p>
            </div>
          ) : (
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {entries.map(entry => (
                  <div key={entry.id} className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">
                        {new Date(entry.date).toLocaleDateString()}
                      </span>
                      <Badge className={getSeverityBg(entry.overall)}>
                        Overall: {entry.overall}/10
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(entry.symptoms).map(([key, value]) => {
                        const symptom = SYMPTOMS.find(s => s.id === key);
                        return (
                          <Badge key={key} variant="outline" className="text-xs">
                            {symptom?.name}: {value}
                          </Badge>
                        );
                      })}
                    </div>
                    {entry.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{entry.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Today's Symptoms</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {SYMPTOMS.map(symptom => {
              const Icon = symptom.icon;
              return (
                <div key={symptom.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: symptom.color }} />
                      {symptom.name}
                    </Label>
                    <span className={`font-bold ${getSeverityColor(currentSymptoms[symptom.id])}`}>
                      {currentSymptoms[symptom.id]}/10
                    </span>
                  </div>
                  <Slider
                    value={[currentSymptoms[symptom.id]]}
                    onValueChange={([value]) => 
                      setCurrentSymptoms(prev => ({ ...prev, [symptom.id]: value }))
                    }
                    max={10}
                    step={1}
                    className="cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>None</span>
                    <span>Severe</span>
                  </div>
                </div>
              );
            })}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any additional observations..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry}>
              <Save className="w-4 h-4 mr-2" />
              Save Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SymptomDiary;
